import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Fade from '@mui/material/Fade';
import { FaTools, FaUserTie, FaHandshake, FaSearch } from 'react-icons/fa';

const Feature = ({ title, text, icon: IconComponent, href }) => {
  const accentColor = 'primary.main'; // Primary Green

  const CardComponent = (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 4,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          borderColor: accentColor,
        },
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          p: { xs: 3, md: 4 },
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: accentColor,
            borderRadius: '50%',
            bgcolor: (t) => t.palette.mode === 'dark' ? 'hsla(145, 72%, 40%, 0.1)' : t.palette.primary[50],
            mb: 3,
            transition: 'all 0.3s ease',
            '.MuiCard-root:hover &': {
              bgcolor: accentColor,
              color: 'primary.contrastText',
              transform: 'scale(1.1)',
            }
          }}
        >
          <IconComponent size={36} />
        </Box>
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 2,
            color: 'text.primary',
            fontWeight: 700,
            letterSpacing: '-0.5px'
          }}
        >
          {title}
        </Typography>
        <Typography 
          variant="body1"
          sx={{ 
            color: 'text.secondary',
            lineHeight: 1.6
          }}
        >
          {text}
        </Typography>
      </CardContent>
    </Card>
  );

  return href ? (
    <Link to={href} style={{ textDecoration: 'none', display: 'block', height: '100%' }}>
      {CardComponent}
    </Link>
  ) : (
    CardComponent
  );
};

const TITLES = [
  { prefix: "Find Your Next ", highlight: "Opportunity" },
  { prefix: "Your Hub for ", highlight: "Skilled Labour" }
];

const Home = () => {
  const [titleIndex, setTitleIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setTitleIndex((prev) => (prev + 1) % TITLES.length);
        setFadeIn(true);
      }, 500);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const bgGradient = (theme) => theme.palette.mode === 'dark' ? 'none' : `linear-gradient(135deg, ${theme.palette.primary[50]} 0%, ${theme.palette.success.light}22 100%)`;

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: bgGradient,
          minHeight: '100vh',
          py: { xs: 10, md: 20 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: (theme) => `radial-gradient(${theme.palette.primary.main} 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
            opacity: 0.05,
            pointerEvents: 'none',
          }
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, md: 4 } }}>
          <Stack
            alignItems="center"
            spacing={{ xs: 4, md: 5 }}
            sx={{ textAlign: 'center' }}
          >
            <Fade in={fadeIn} timeout={500}>
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
                  lineHeight: 1.1,
                  color: 'text.primary',
                }}
              >
                {TITLES[titleIndex].prefix}
                <Box
                  component="span"
                  sx={{ color: 'primary.main', display: 'inline' }}
                >
                  {TITLES[titleIndex].highlight}
                </Box>
              </Typography>
            </Fade>
            <Typography
              variant="h5"
              sx={{
                color: 'text.secondary',
                maxWidth: '800px',
                mx: 'auto',
                mt: 3,
                fontSize: { xs: '1.1rem', md: '1.5rem' },
                lineHeight: 1.6,
              }}
            >
              The simple, direct way to connect with jobs and workers in construction, cleaning, and maintenance.
            </Typography>
            <Stack spacing={3} direction={{ xs: 'column', sm: 'row' }}>
              <Button
                component={Link}
                to="/jobs"
                variant="contained"
                size="large"
                sx={{
                  borderRadius: 8,
                  px: 4,
                  py: 1.5,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  fontSize: '1.1rem',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                }}
              >
                Find Jobs
              </Button>
              <Button
                component={Link}
                to="/dashboard"
                variant="contained"
                size="large"
                sx={{
                  borderRadius: 8,
                  px: 4,
                  py: 1.5,
                  bgcolor: 'secondary.main',
                  color: 'secondary.contrastText',
                  fontSize: '1.1rem',
                  '&:hover': {
                    bgcolor: 'secondary.dark',
                  },
                }}
              >
                Hire Workers
              </Button>
            </Stack>
          </Stack>

          {/* Features Section */}
          <Grid 
            container 
            spacing={3}
            sx={{ 
              mt: { xs: 8, md: 12 },
            }}
          >
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Feature
                icon={FaSearch}
                title="Easy Search"
                text="Browse available jobs and candidates to find the right fit for your needs."
                href="/jobs"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Feature
                icon={FaTools}
                title="Skilled Workers"
                text="Access a community of experienced professionals ready to work."
                href="/dashboard"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Feature
                icon={FaUserTie}
                title="Active Employers"
                text="Connect directly with companies looking for talent like you."
                href="/dashboard"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Feature
                icon={FaHandshake}
                title="Direct Connection"
                text="Simple application process connecting you directly with the other party."
                href="/dashboard"
              />
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;